/**
 * Normalizes a website URL by adding https:// if no protocol is provided
 * @param website - The website URL or domain
 * @returns Normalized URL with protocol
 */
export const normalizeWebsiteUrl = (website: string): string => {
  if (!website.trim()) return '';

  const trimmed = website.trim();

  // If already has protocol, return as is
  if (trimmed.match(/^https?:\/\//i)) {
    return trimmed;
  }

  // Add https:// prefix
  return `https://${trimmed}`;
};

/**
 * Validates if a website URL is in a valid format
 * Accepts both full URLs and domain names
 * @param website - The website URL or domain
 * @returns true if valid, false otherwise
 */
export const isValidWebsite = (website: string): boolean => {
  if (!website.trim()) return true; // Empty is valid (optional field)

  const trimmed = website.trim();

  // Check if it's a valid domain name or URL
  const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}$/;
  const subdomainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.([a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}|[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,})$/;

  // If it has protocol, validate as URL
  if (trimmed.match(/^https?:\/\//i)) {
    try {
      new URL(trimmed);
      return true;
    } catch {
      return false;
    }
  }

  // Otherwise validate as domain
  return domainRegex.test(trimmed) || subdomainRegex.test(trimmed);
};

/**
 * Formats a website URL for display (removes protocol if present)
 * @param website - The website URL
 * @returns Domain without protocol for display
 */
export const formatWebsiteForDisplay = (website: string): string => {
  if (!website) return '';
  return website.replace(/^https?:\/\//, '');
};